import { useEffect, useState } from 'react'
import { Button, HStack, Text, VStack } from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { createFeedback, updateFeedback } from 'API/feedback'
import SvgIcon from 'components/SvgIcon'
import { EQualityOfSearchFeature } from 'constants/user'
import { DARK_GREY, DARK_TEXT_PRIMARY } from 'constants/dark-tokens'
import FeedbackDetail from '../FeedbackDetail'

interface UserFeedbackProps {
  // Opt-in dark-surface styling (default light) for the RallyPD dark search page.
  dark?: boolean
}

const UserFeedback = ({ dark = false }: UserFeedbackProps) => {
  const { authStore, searchStore } = useStores()
  const { userDetail } = authStore
  const { searchKeyword } = searchStore
  const [feedbackId, setFeedbackId] = useState<number>()
  const [showFeedbackDetail, setShowFeedbackDetail] = useState<boolean>(false)
  const [isOpenFeedbackModal, setIsOpenFeedbackModal] = useState<boolean>(false)

  async function handleVoteFeedback(value: EQualityOfSearchFeature): Promise<void> {
    setShowFeedbackDetail(true)
    const feedback = await createFeedback({
      searchKeyword,
      userId: userDetail?.id,
      qualityOfSearchFeature: value
    })
    setFeedbackId(feedback?.id)
  }

  async function handleSubmitFeedback(text: string): Promise<void> {
    if (feedbackId) {
      await updateFeedback(feedbackId, { text })
      setFeedbackId(undefined)
      setShowFeedbackDetail(false)
    }
  }

  useEffect(() => {
    setFeedbackId(undefined)
    setShowFeedbackDetail(false)
  }, [searchKeyword])

  return (
    <HStack
      width="full"
      height="80px"
      justify="space-between"
      background={dark ? 'rgba(255,255,255,0.04)' : 'gray.200'}
      borderRadius={6}
      borderLeft={`4px solid ${dark ? DARK_GREY : '#718096'}`}
      padding={4}
      _hover={{ cursor: showFeedbackDetail && 'pointer' }}
      onClick={() => showFeedbackDetail && setIsOpenFeedbackModal(true)}
    >
      {showFeedbackDetail ? (
        <HStack fontSize="16px" fontWeight={700} lineHeight={6} spacing={0}>
          <HStack spacing={1}>
            <SvgIcon size={16} iconName="circle-check" color={dark ? DARK_TEXT_PRIMARY : 'gray.700'} />
            <Text color={dark ? DARK_TEXT_PRIMARY : 'gray.700'}>Thank you for your feedback.</Text>
          </HStack>
          <Text color="primary.500" cursor="pointer" textDecoration="underline">
            Tell us why
          </Text>
        </HStack>
      ) : (
        <>
          <VStack width="full" align="flex-start" fontSize={{ base: 'sm', md: 'md' }} spacing={0}>
            <Text color={dark ? DARK_TEXT_PRIMARY : 'gray.700'} fontWeight={700} lineHeight={6}>
              How’s your search going?
            </Text>
          </VStack>
          <HStack spacing={4}>
            <Button
              width={{ base: '48px', lg: '300px' }}
              height="48px"
              variant="solid"
              padding={0}
              fontSize={{ base: '0', lg: '16px' }}
              fontWeight={600}
              color={dark ? DARK_TEXT_PRIMARY : undefined}
              background={dark ? 'transparent' : 'gray.200'}
              gap={{ base: 0, lg: 2 }}
              border={`2px solid ${dark ? DARK_GREY : '#F7FAFC'}`}
              _hover={{ background: dark ? 'rgba(255,255,255,0.06)' : 'gray.50' }}
              _active={{ background: dark ? 'rgba(255,255,255,0.06)' : 'gray.50' }}
              onClick={() => handleVoteFeedback(EQualityOfSearchFeature.GOOD)}
            >
              <SvgIcon size={20} iconName="thumb-up" color={dark ? DARK_TEXT_PRIMARY : undefined} />
              Helpful
            </Button>
            <Button
              width={{ base: '48px', lg: '300px' }}
              height="48px"
              variant="solid"
              padding={0}
              fontSize={{ base: '0', lg: '16px' }}
              fontWeight={600}
              color={dark ? DARK_TEXT_PRIMARY : undefined}
              background={dark ? 'transparent' : 'gray.200'}
              gap={{ base: 0, lg: 2 }}
              border={`2px solid ${dark ? DARK_GREY : '#F7FAFC'}`}
              _hover={{ background: dark ? 'rgba(255,255,255,0.06)' : 'white' }}
              onClick={() => handleVoteFeedback(EQualityOfSearchFeature.BAD)}
            >
              <SvgIcon size={20} iconName="thumb-down" color={dark ? DARK_TEXT_PRIMARY : undefined} />I can not find what I need
            </Button>
          </HStack>
        </>
      )}
      <FeedbackDetail
        isOpen={isOpenFeedbackModal}
        onClose={() => setIsOpenFeedbackModal(false)}
        handleSubmitFeedback={handleSubmitFeedback}
      />
    </HStack>
  )
}

export default observer(UserFeedback)
